import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

// import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Heading2 } from '@ui/Typography'
import { Chip } from '@ui/components/Chip'
import { Tooltip } from '@ui/components/Tooltip'

import { useWindowResize } from '@utils/domUtils'

import { WalletTypeEnum } from '@model/WalletDto'
import type { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { useFundingExit } from '@hooks/useFundingExit'
import { useTradingAccountStatus } from '@hooks/useTradingAccountStatus'

import { Box } from '@shared/components/Box'
import { FundingMethodList } from '@shared/components/Funding/FundingMethodList'
import { Loading } from '@shared/components/Loading/Loading'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

// import { useWithdrawalTracker } from '@analytics/amplitude/withdrawal'

import { useWalletCurrencyIcons } from '../shared/hooks/useWalletCurrencyIcons'
import { useWithdrawalMethods } from './hooks/useWithdrawalMethods'
import { useWithdrawalSelections } from './hooks/useWithdrawalSelections'
import {
  getWithdrawalWalletDisabledReason,
  useWithdrawalWallets,
} from './hooks/useWithdrawalWallets'

const getWalletType = (
  dashboardContext: WithdrawalPageProps['dashboardContext'],
  isCFD: boolean
): WalletTypeEnum => {
  if (dashboardContext === 'payment-agent') {
    return WalletTypeEnum.PAYMENT_AGENT
  }

  if (dashboardContext === 'introducing-broker') {
    return WalletTypeEnum.IB
  }

  return isCFD ? WalletTypeEnum.CFD : WalletTypeEnum.ETD
}

interface WithdrawalPageProps {
  dashboardContext?: 'traders-room' | 'payment-agent' | 'introducing-broker'
}

export const WithdrawalPage: React.FC<WithdrawalPageProps> = ({
  dashboardContext = 'traders-room',
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // const location = useLocation()
  const locale = useSessionLanguage()
  const { isDefaultCFDProductType } = useProductReadContext()
  // const tracker = useWithdrawalTracker()
  const isMobile = useWindowResize()

  const isCFD = isDefaultCFDProductType()
  const { handleExit } = useFundingExit()

  const resolvedWalletType = getWalletType(dashboardContext, isCFD)

  const { wallets, isWalletsLoading } = useWithdrawalWallets(resolvedWalletType)
  const { hasNegativeBalance: hasNegativeEquity, isLoading: isEquityLoading } =
    useTradingAccountStatus()
  const hasNegativeEquityRestriction =
    resolvedWalletType === WalletTypeEnum.CFD && hasNegativeEquity

  const { selectedWalletId, setSelectedWalletId, selectedWallet, locationState } =
    useWithdrawalSelections({
      wallets,
      hasNegativeEquity: hasNegativeEquityRestriction,
      isWalletsLoading,
      isEquityLoading,
      isCFD,
    })

  const manualSelectionRef = useRef<string | null>(null)
  const trackedWalletIdRef = useRef<string | null>(null)

  // useEffect(() => {
  //   tracker.start({
  //     entry_point: dashboardContext === 'payment-agent' ? 'payment_agent' : 'direct_link',
  //     source_page: location.pathname,
  //     preset_wallet_id: locationState.selectedWalletId,
  //   })
  // }, [tracker])

  useEffect(() => {
    if (!selectedWallet) return
    if (trackedWalletIdRef.current === selectedWallet.id) return

    // const selectionType = manualSelectionRef.current === selectedWallet.id ? 'manual' : 'preset'

    // tracker.walletSelected({
    //   wallet_id: selectedWallet.id,
    //   currency: selectedWallet.currency.id,
    //   selection_type: selectionType,
    // })

    trackedWalletIdRef.current = selectedWallet.id
    manualSelectionRef.current = null
  }, [selectedWallet])

  const { methods, isMethodsLoading } = useWithdrawalMethods({
    locale,
    walletId: selectedWallet?.id,
  })

  const walletCurrencyIcons = useWalletCurrencyIcons(wallets)

  const isInitialLoading = isWalletsLoading || (isCFD && isEquityLoading)

  const negativeEquityTooltip = t(
    'Wallet.Withdrawal is currently not available because your trading account'
  )
  const unavailableTooltip = t('Withdrawals for this wallet are unavailable.')

  return (
    <FundingTemplate
      isLoading={isInitialLoading}
      onBack={handleExit}
      maxWidth={580}
      header={
        <Box flex direction='column' gap='x24' alignSelf='stretch'>
          <Heading2 align='center' medium>
            {t('Withdraw funds')}
          </Heading2>

          {!locationState.isDestinationPreselected && (
            <Box flex direction='column' gap='x16'>
              <Box flex py='x4' gap='x12' justify='center' wrap>
                {wallets.map((w) => {
                  const disabledReason = getWithdrawalWalletDisabledReason(
                    w,
                    hasNegativeEquityRestriction
                  )
                  const isDisabled = disabledReason !== null
                  const tooltipContent =
                    disabledReason === 'negative-equity'
                      ? negativeEquityTooltip
                      : unavailableTooltip

                  const Icon = walletCurrencyIcons[w.currency.id]

                  return (
                    <Tooltip key={w.id} content={tooltipContent} disabled={!isDisabled}>
                      <Chip
                        size='sm'
                        selected={!isDisabled && w.id === selectedWalletId}
                        disabled={isDisabled && !isMobile}
                        aria-disabled={isDisabled}
                        iconLeft={Icon}
                        onClick={() => {
                          if (isDisabled) return
                          manualSelectionRef.current = w.id
                          setSelectedWalletId(w.id)
                        }}
                      >
                        {`${w.currency.id} ${t('Wallet.Wallet')}`}
                      </Chip>
                    </Tooltip>
                  )
                })}
              </Box>
            </Box>
          )}
        </Box>
      }
    >
      <Loading isLoading={isMethodsLoading} showLoadingIcon>
        <FundingMethodList
          variant='withdrawal'
          methods={methods}
          disabled={!selectedWallet}
          onSelect={(provider: WalletPaymentProvider) => {
            if (!selectedWallet) return

            // tracker.methodSelected({
            //   method_id: provider.method.id,
            //   method_type: provider.method.name ?? '',
            //   provider: provider.name,
            //   is_last_used: Boolean(provider.lastDeposit),
            // })
            // tracker.stepChanged({
            //   from: 'wallet_method',
            //   to: 'amount_terms',
            //   direction: 'forward',
            // })

            navigate(
              `/dashboard/${dashboardContext}/withdrawal/wallet/${selectedWallet.id}/${provider.method.id}/amount`,
              {
                state: {
                  ...locationState,
                  walletPaymentProvider: provider,
                  selectedWalletId: selectedWallet.id,
                },
              }
            )
          }}
        />
      </Loading>
    </FundingTemplate>
  )
}
